/**
 * Sprite Upgrade Service
 * Utilities for sprite context menu, notifications, and create-video redirect
 */

import { interpolate } from '../../../translations.js';
import { showConfirm } from '../../../utils/UnifiedNotificationSystem.js';
import { storage } from '../../../utils/storage.js';
import { navigateToCreateVideo } from '../../../../components/navigation/hangoutActions.js';

/**
 * Show notification using the unified notification system
 * @param {string} message - Notification message
 * @param {'success'|'error'|'info'|'warning'} type - Notification type
 * @param {object} [options] - Passed to UnifiedNotificationSystem (e.g. duration)
 * @returns {Promise<void>}
 */
export const showNotification = async (message, type = 'info', options = {}) => {
  try {
    const { showSuccess, showError, showWarning, showInfo } = await import('../../../utils/UnifiedNotificationSystem.js');
    switch (type) {
      case 'success': showSuccess(message, options); break;
      case 'error': showError(message, options); break;
      case 'warning': showWarning(message, options); break;
      default: showInfo ? showInfo(message, options) : showWarning(message, options);
    }
  } catch {
    console.warn('[spriteUpgradeService] Notification fallback:', message);
  }
};

/**
 * Parse sprite URL to extract character, outfit, and action IDs
 * Expected URL format: .../sprites/{character_id}/{outfit_id}/{action_id}/...
 * @param {string} spriteUrl
 * @returns {{characterId: string|null, outfitId: string|null, actionId: string|null}}
 */
export const parseSpriteUrl = (spriteUrl) => {
  const empty = { characterId: null, outfitId: null, actionId: null, videoActionId: null };
  if (!spriteUrl) return empty;

  const videoMatch = String(spriteUrl).match(/\/s\/videos\/(\d+)_(\d+)_(\d+)_(\d+)\.webm/i);
  if (videoMatch) {
    return {
      characterId: videoMatch[1],
      outfitId: videoMatch[2],
      actionId: videoMatch[3],
      videoActionId: videoMatch[4],
    };
  }

  // Try pattern: /sprites/{char_id}/{outfit_id}/{action_id}/
  const spritesMatch = spriteUrl.match(/\/sprites\/(\d+)\/(\d+)\/(\d+)/);
  if (spritesMatch) {
    return {
      characterId: spritesMatch[1],
      outfitId: spritesMatch[2],
      actionId: spritesMatch[3],
      videoActionId: null,
    };
  }

  // Try pattern: character_{id}_outfit_{outfit}_action_{action}
  const filenameMatch = spriteUrl.match(/character[_-]?(\d+)[_-]?outfit[_-]?(\d+)[_-]?action[_-]?(\d+)/i);
  if (filenameMatch) {
    return {
      characterId: filenameMatch[1],
      outfitId: filenameMatch[2],
      actionId: filenameMatch[3],
      videoActionId: null,
    };
  }

  // Try pattern: /cdn/characters/{char_id}/sprites/{outfit_id}/{action_id}/
  const cdnMatch = spriteUrl.match(/\/characters\/(\d+)\/sprites\/(\d+)\/(\d+)/);
  if (cdnMatch) {
    return {
      characterId: cdnMatch[1],
      outfitId: cdnMatch[2],
      actionId: cdnMatch[3],
      videoActionId: null,
    };
  }

  return empty;
};

/**
 * Get current language from storage or default to 'en'
 * @returns {string}
 */
const getCurrentLanguage = () => storage.getLanguage();

/**
 * Get translation string from the translation system
 * @param {string} key - Translation key (e.g., 'sprite_context.upgrade_to_video')
 * @param {object} params - Optional parameters for interpolation
 * @returns {string}
 */
export const getTranslation = (key, params = {}) => {
  try {
    const lang = getCurrentLanguage();
    const translations = window.translations?.[lang]?.hangout || window.translations?.en?.hangout || {};
    
    // Navigate nested keys (e.g., 'sprite_context.upgrade_to_video')
    const keys = key.split('.');
    let value = translations;
    for (const k of keys) {
      if (value && typeof value === 'object' && k in value) {
        value = value[k];
      } else {
        return key; // Key not found, return original key
      }
    }
    
    if (typeof value === 'string' && params) value = interpolate(value, params);

    return typeof value === 'string' ? value : key;
  } catch {
    return key;
  }
};

export const resolveTranslation = (key, fallback) => {
  const value = getTranslation(key);
  return value && value !== key ? value : fallback;
};

/**
 * Confirm leaving hangout, then open create-video with character, outfit, and action prefilled.
 * @param {{characterId?: string|number, outfitId?: string|number, actionId?: string|number}} spriteData
 * @param {'create'|'upgrade'} [intent='create']
 * @returns {Promise<void>}
 */
export const confirmRedirectToCreateVideo = async (spriteData, intent = 'create') => {
  if (!spriteData?.characterId || spriteData?.actionId === null || spriteData?.actionId === undefined) {
    await showNotification('Could not determine sprite details. Please try again.', 'error');
    return;
  }

  const confirmed = await showConfirm(
    resolveTranslation(
      'sprite_context.redirect_to_create_video',
      'You will be redirected to the create video page'
    ),
    {
      yesText: resolveTranslation('common.yes', 'Yes'),
      noText: resolveTranslation('common.cancel', 'Cancel'),
      type: 'info',
    }
  );
  if (!confirmed) return;

  const resolvedIntent = intent === 'upgrade' ? 'upgrade' : 'create';
  navigateToCreateVideo(
    String(spriteData.characterId),
    String(spriteData.outfitId ?? '0'),
    String(spriteData.actionId),
    resolvedIntent
  );
};
